import { logger } from '@/logging/logger';
import type { SgWay } from '@/models/way';
import { quote$, quoteErrors$ } from '@/services/streams.service';
import { getMyHedgeTrade } from '@/store/app.selectors';
import { type AddErrorAction, addErrorAction } from '@/store/state/errors/errors.actions';
/* eslint-disable default-param-last */
import { type Epic, ofType } from 'redux-observable';
import { type SchedulerLike, merge, of, timer } from 'rxjs';
import { filter, map, mergeMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import type { AppAction } from '../app.actions';
import type { AppState } from '../app.state';
import {
  AmericanForwardQuoteReceivedAction,
  type ExecuteAction,
  type QuotingTimeoutAction,
  type RequestForStreamAction,
  type StreamRequestedAction,
  type StreamStoppedAction,
  cashQuoteReceivedAction,
  quotingTimeoutAction,
  requestForStreamAction,
  swapQuoteReceivedAction,
} from '../state/rfs/rfs.actions';
import { getNewRfsId } from './request-for-stream.epic';

const MAX_PREDELIVER_RETRY = 1;
const PREDELIVER_RETRY_DELAY = 1000;

function myHedgeSideToSgWay(side: 'buy' | 'sell' | undefined): SgWay {
  switch (side) {
    case 'buy':
      return 'Buy';
    case 'sell':
      return 'Sell';
    case undefined:
      return 'Both';
    default: {
      const exhaustiveCheck: never = side;
      return exhaustiveCheck;
    }
  }
}

export const quotesEpic =
  (getQuote$ = quote$, getQuoteErrors$ = quoteErrors$, newRfsId = getNewRfsId, scheduler?: SchedulerLike): Epic<AppAction, AppAction, AppState> =>
  (action$, state$) =>
    action$.pipe(
      ofType<AppAction, StreamRequestedAction>('STREAM_REQUESTED'),
      mergeMap((action) =>
        merge(
          getQuote$().pipe(
            withLatestFrom(state$),
            filter(([quote, state]) => {
              const quoteId = Number(quote.quoteId);
              const isPositifQuoteId = Number.isNaN(quoteId) ? true : quoteId > -1;

              return quote.rfsId === action.rfsId && (state.rfs.status === 'quoting' || state.rfs.status === 'requesting') && state.rfs.id === quote.rfsId && isPositifQuoteId;
            }),
            map(([quote, state]) => {
              switch (quote.type) {
                case 'CASH.QUOTE.REPLY':
                  return cashQuoteReceivedAction(
                    quote.rfsId,
                    quote.quoteId,
                    quote.spotWithMargin,
                    quote.contraAmount,
                    quote.forwardPointsWithMargin,
                    quote.forwardWithMargin,
                    quote.currencyPair,
                    quote.sgWay,
                    quote.timestamp,
                  );
                case 'AMERICANFORWARD.QUOTE.REPLY':
                  return AmericanForwardQuoteReceivedAction(quote.quoteId, Object.values(quote.legsData)[0]?.solving, quote.timestamp);

                default:
                  return swapQuoteReceivedAction(
                    quote.rfsId,
                    quote.quoteId,
                    quote.spot,
                    quote.contraAmount,
                    quote.nearPoints,
                    quote.nearPrice,
                    quote.farPoints,
                    quote.farPrice,
                    quote.currencyPair,
                    myHedgeSideToSgWay(getMyHedgeTrade(state)?.side),
                    quote.historicalDealOffRate,
                    quote.timestamp,
                  );
              }
            }),
          ),
          getQuoteErrors$().pipe(
            // tap(quote => logger.logInfo('quoteErrors$: {@quote}', quote)),
            tap((quote) => logger.logInformation('quoteErrors$', quote)),
            mergeMap((quote) => {
              if (quote.errorCode === 'EM_TIMEOUT') {
                return of(quotingTimeoutAction());
              }

              const isPredliverLockError =
                action.product === 'FxPredeliver' && quote.errorCode === 'EM_FINANCIAL_CHECKS_FAILED' && quote.errorMessage === 'Historical deal was not returned by XONE';

              const shouldRetry = isPredliverLockError && action.retryCount < MAX_PREDELIVER_RETRY;

              // shouldRetry && logger.logInfo('Predliver lock error, will retry');
              shouldRetry && logger.logInformation('Predliver lock error, will retry');

              return shouldRetry
                ? timer(PREDELIVER_RETRY_DELAY, undefined, scheduler).pipe(map((_) => requestForStreamAction(newRfsId(), action.retryCount + 1)))
                : of(
                    addErrorAction({
                      code: quote.errorCode,
                      message: quote.errorMessage,
                      multipassErrorCode: quote.multipassErrorCode,
                      creditCheckType: quote.creditCheck?.type,
                      creditCheckLimit: quote.creditCheck?.limit,
                    }),
                  );
            }),
          ),
        ).pipe(
          takeUntil(
            action$.pipe(
              ofType<AppAction, QuotingTimeoutAction | ExecuteAction | StreamStoppedAction | AddErrorAction | RequestForStreamAction>(
                'QUOTING_TIMEOUT',
                'EXECUTE',
                'STREAM_STOPPED',
                'ADD_ERROR',
                'REQUEST_FOR_STREAM',
              ),
            ),
          ),
        ),
      ),
    );
